<template>
  <div class="first-run" data-test="first-run-panel">
    <div class="first-run__title">Run this on your page</div>
    <div class="first-run__sub">Same report, your URL and keyword. Pick a plan on the next step. Both fields are saved so nothing is retyped.</div>
    <input v-model.trim="url" class="first-run__field" type="url" placeholder="https://yoursite.com/page" autocomplete="url" data-test="first-run-url" @keyup.enter="submit" />
    <input v-model.trim="keyword" class="first-run__field" type="text" placeholder="Target keyword" data-test="first-run-keyword" @keyup.enter="submit" />
    <div v-if="error" class="first-run__err">{{ error }}</div>
    <button type="button" class="first-run__btn" :disabled="!canSubmit || busy" data-test="first-run-submit" @click="submit">
      {{ busy ? 'Saving' : 'Run my report' }}
    </button>
    <div class="first-run__fine">From {{ basicPrice }} / month. Full refund within 7 days.</div>
  </div>
</template>

<script>
import { formatPrice, planById } from './plans'
import { isValidPageUrl, normalizePageUrl, pendingFirstRun } from './first-run'
import { segmentTrack } from '@/utils'

// Conversion box under the example report of a no-plan account: saves the URL and keyword
// (PUT /api/users/pending-first-run/) and goes to the plan picker; the first real run is
// prefilled from them right after checkout.
export default {
  name: 'FirstRunIntentPanel',
  data() {
    const saved = pendingFirstRun(this.$store) || {}
    return { url: saved.url || '', keyword: saved.keyword || '', busy: false, error: '' }
  },
  computed: {
    canSubmit() {
      return isValidPageUrl(this.url) && this.keyword.length >= 2
    },
    basicPrice() {
      const basic = planById('basic')
      return formatPrice(basic && basic.prices ? basic.prices.month : 40)
    },
  },
  methods: {
    async submit() {
      if (!this.canSubmit || this.busy) return
      this.busy = true
      this.error = ''
      const run = { url: normalizePageUrl(this.url), keyword: this.keyword }
      try {
        const response = await this.$store.dispatch('setPendingFirstRun', run)
        const data = response.data || {}
        if (data.status !== 'SUCCESS') {
          this.error = data.msg || 'Could not save that. Please check the URL and try again.'
          return
        }
        this.$store.commit('signup/patch', { firstRun: run })
        if (data.user) {
          this.$store.commit('setUser', data.user)
        } else {
          this.$store.commit('setUser', { ...this.$store.state.user, pendingFirstRun: { ...run, savedAt: new Date().toISOString() } })
        }
        segmentTrack('first_run_intent_saved', { keyword: run.keyword })
        this.$router.push('/plans')
      } catch (e) {
        console.log(e)
        this.error = 'Could not save that. Please try again.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './auth-tokens.scss';

.first-run {
  background: $pop-teal; color: #fff; border-radius: 18px; padding: 24px; margin: 24px 0 40px; max-width: 560px;

  &__title { font-family: 'Mulish', sans-serif; font-weight: 800; font-size: 17px; margin-bottom: 4px; }
  &__sub { font-size: 13px; opacity: 0.8; line-height: 1.5; margin-bottom: 14px; }
  &__field {
    display: block; width: 100%; box-sizing: border-box; font-family: inherit; font-size: 15px; color: $pop-teal;
    background: #fff; border: 0; border-radius: 12px; padding: 15px 16px; margin-bottom: 8px; outline: none;
    &:focus { box-shadow: $auth-focus-ring; }
    &::placeholder { color: $pop-ink-faint; }
  }
  &__err { font-size: 13px; color: #ffd0d0; margin: 2px 0 8px; }
  &__btn {
    display: block; width: 100%; margin-top: 4px; padding: 13px 16px; border: 0; border-radius: 12px; cursor: pointer;
    font-family: inherit; font-weight: 700; font-size: 14.5px; background: $pop-accent; color: #fff; box-shadow: $auth-primary-shadow;
    &[disabled] { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
  }
  &__fine { font-size: 12px; opacity: 0.7; margin-top: 10px; text-align: center; }
}
</style>
